'use client';

import React from 'react';
import { cj } from 'src/utils/classNames';
import { Wallet, PointsMono, Details } from '@velocity-exchange/icons';
import {
	ActionButtonConfig,
	EarnAccordionOptions,
} from 'src/hooks/useEarnProduct';
import { EarnActionButton } from 'src/components/Earn/EarnActionButton';
import MarketIcon from '../../Utils/MarketIcon';
import ApyBreakdownTooltipV2 from '../../Tooltip/ApyBreakdownTooltipV2';
import PresetTag from 'src/components/Tags/PresetTag';
import { useEarnProductData } from 'src/hooks/useEarnProductData';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { getColorVar } from 'src/utils/theme/colorVars';

interface EarnProductMobileProps {
	earnProduct: EarnAccordionOptions;
	showDetails: boolean;
	noWrapper?: boolean;
}

export const EarnProductMobile: React.FC<EarnProductMobileProps> = ({
	earnProduct,
	showDetails,
	noWrapper,
}) => {
	const {
		cleanedMarketSymbol,
		finalProductData,
		apyData,
		shouldShowWalletBalance,
		getWalletBalance,
		apyStr,
	} = useEarnProductData(earnProduct);

	const isStrategyVault = earnProduct.productType === 'strategy_vaults';

	const filteredActionButtons = earnProduct.actionButtons.filter(
		(button: ActionButtonConfig) => button.type !== 'details'
	);

	return (
		<div
			className={cj(
				'flex flex-col gap-y-md bg-container-bg',
				!showDetails && 'border-t border-stroke-divider',
				showDetails && 'rounded-sm',
				noWrapper ? 'p-md' : 'py-lg'
			)}
		>
			<div className="flex items-center justify-between">
				{showDetails ? (
					<>
						<div className="flex flex-1 items-center gap-md">
							<div className="inline-flex items-center">
								<MarketIcon
									sizeClass="min-w-[40px] min-h-[40px]"
									marketSymbol={cleanedMarketSymbol}
								/>
							</div>
							<div className="flex flex-1 flex-col justify-center gap-xs">
								<div className="flex items-center gap-sm">
									<div className="typo-label-md text-text-default">
										{cleanedMarketSymbol}
									</div>
								</div>

								<div className="flex items-center gap-sm">
									<div className="typo-label-md whitespace-nowrap text-text-default">
										{finalProductData.title}
									</div>
									{finalProductData.isNew && <PresetTag preset="new" />}
								</div>
								{shouldShowWalletBalance && (
									<div className="flex items-center gap-xs">
										<Wallet color="var(--color-text-secondary)" />
										<div className="block typo-body-xs text-text-secondary">
											{getWalletBalance(cleanedMarketSymbol)}
										</div>
									</div>
								)}
							</div>
						</div>
						<div className="flex items-center gap-xs">
							{(finalProductData.promoApy ?? 0) > 0 && (
								<PointsMono size={16} color={'url(#icon-gradient)'} />
							)}
							<div className="flex flex-col gap-xs">
								<span className="text-end text-text-secondary">
									{earnProduct.isRangeApy &&
									!(
										earnProduct.productType === 'strategy_vaults' &&
										earnProduct.apyToShow > 30
									)
										? 'Up to'
										: ''}
								</span>
								<ApyBreakdownTooltipV2
									className="text-text-secondary"
									apyChunks={apyData.deposit.chunks}
									showTooltip={apyData.deposit.showTooltip && !isStrategyVault}
								>
									<span className="typo-label-md text-positive-green">
										{apyStr}
									</span>
								</ApyBreakdownTooltipV2>
							</div>
						</div>
					</>
				) : (
					<>
						<span className="typo-label-md text-text-default">
							{finalProductData.title}
						</span>

						<div className="flex items-center gap-xs">
							{(finalProductData.promoApy ?? 0) > 0 && (
								<PointsMono size={16} color={getColorVar('purple-link')} />
							)}
							<div className="flex flex-col gap-xs">
								<span className="text-end text-text-secondary">
									{earnProduct.isRangeApy &&
									!(
										earnProduct.productType === 'strategy_vaults' &&
										earnProduct.apyToShow > 30
									)
										? 'Up to'
										: ''}
								</span>
								<ApyBreakdownTooltipV2
									className="text-text-secondary"
									apyChunks={apyData.deposit.chunks}
									showTooltip={apyData.deposit.showTooltip && !isStrategyVault}
								>
									<span className="typo-label-md text-positive-green">
										{apyStr}
									</span>
								</ApyBreakdownTooltipV2>
							</div>
						</div>
					</>
				)}
			</div>

			<div className="flex w-full items-center justify-between gap-md">
				<div className="inline-flex flex-1 items-center space-x-sm">
					{finalProductData.apyInfoAction && (
						<IconButton
							size="sm"
							onClick={finalProductData.apyInfoAction}
							icon={Details}
						></IconButton>
					)}
					{filteredActionButtons.map(
						(actionButton: ActionButtonConfig, buttonIndex: number) => {
							const widthClass =
								filteredActionButtons.length === 1 ? 'w-full' : 'flex-1';

							return (
								<EarnActionButton
									productType={earnProduct.productType}
									key={`action_button_${buttonIndex}`}
									type={actionButton.type}
									marketIndex={earnProduct.marketIndex}
									marketSymbol={earnProduct.marketSymbol}
									highlight={actionButton.highlight}
									mobileText={actionButton.mobileText}
									className={cj(widthClass, 'h-2xl')}
									size="MEDIUM"
								/>
							);
						}
					)}
				</div>
			</div>
		</div>
	);
};
